import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Typo } from "@ui/text/Typo"
import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { GD } from "src/GD"
import { getFriendlyErrorMessage } from "src/utils/errors"
import { MOBILE_VIEWPORT_MEDIA as MOBILE } from "@ui/utils/mobileViewport"
import { DS } from "../ds/Kit"

type PresetEditorMode = "create" | "edit" | null

interface TransactionFilterPresetsProps {
	datePeriodOptions?: ICompanyTransactionFilterOption[]
	currentFilter: ICompanyTransactionFilter
	/** False once the filters are edited by hand, so the picker stops naming a preset. */
	active: boolean
	onApply: (preset: ITransactionFilterPreset) => void
}

const controlStyle = {
	height: "36px",
	minWidth: 0,
	boxSizing: "border-box" as const,
	padding: "0 10px",
	border: `1px solid ${DS.line}`,
	borderRadius: "10px",
	backgroundColor: DS.surface,
	color: DS.ink,
	font: "inherit",
	fontSize: "13px",
}

const withoutComputedFields = (filter: ICompanyTransactionFilter): ICompanyTransactionFilter => {
	const staticFilter = { ...filter }
	delete staticFilter.page
	delete staticFilter.limit
	delete staticFilter.no_limit
	delete staticFilter.from
	delete staticFilter.to
	return staticFilter
}

export const TransactionFilterPresets = ({
	datePeriodOptions = [],
	currentFilter,
	active,
	onApply,
}: TransactionFilterPresetsProps) => {
	const [presets, setPresets] = useState<ITransactionFilterPreset[]>([])
	const [selectedUid, setSelectedUid] = useState("")
	const [editorMode, setEditorMode] = useState<PresetEditorMode>(null)
	const [name, setName] = useState("")
	const [datePeriod, setDatePeriod] = useState<TTransactionDatePeriod | "">("")
	const [dateFrom, setDateFrom] = useState("")
	const [dateTo, setDateTo] = useState("")
	const [busy, setBusy] = useState(false)
	const [deleteArmed, setDeleteArmed] = useState(false)

	const selectedPreset = useMemo(
		() => presets.find((preset) => preset.uid === selectedUid),
		[presets, selectedUid],
	)

	const loadPresets = async (preferredUid?: string) => {
		try {
			const response = await GD.REQ_COMPANY_TRANSACTION_PRESETS.request()
			if (response.error) throw response.error
			const nextPresets = Array.isArray(response.data) ? response.data : []
			setPresets(nextPresets)
			const saved = nextPresets.find((preset) => preset.uid === preferredUid)
			if (saved) {
				setSelectedUid(saved.uid)
				onApply(saved)
			} else setSelectedUid((current) => (nextPresets.some((preset) => preset.uid === current) ? current : ""))
		} catch {
			// Presets are optional UI; transaction filtering remains usable if the migration is not live yet.
			setPresets([])
		}
	}

	useEffect(() => {
		void loadPresets()
	}, [])

	useEffect(() => {
		if (active) return
		setSelectedUid("")
		setDeleteArmed(false)
	}, [active])

	const choosePreset = (uid: string) => {
		setSelectedUid(uid)
		setEditorMode(null)
		setDeleteArmed(false)
		const preset = presets.find((item) => item.uid === uid)
		if (preset) onApply(preset)
	}

	const beginCreate = () => {
		setEditorMode("create")
		setName("")
		setDatePeriod((datePeriodOptions[0]?.value as TTransactionDatePeriod) || "")
		setDateFrom(currentFilter.from || "")
		setDateTo(currentFilter.to || "")
		setDeleteArmed(false)
	}

	const beginEdit = () => {
		if (!selectedPreset?.can_edit) return
		setEditorMode("edit")
		setName(selectedPreset.name)
		setDatePeriod(selectedPreset.date_period)
		setDateFrom(selectedPreset.date_from || selectedPreset.filter.from || "")
		setDateTo(selectedPreset.date_to || selectedPreset.filter.to || "")
		setDeleteArmed(false)
	}

	const submit = async () => {
		const trimmedName = name.trim()
		if (!trimmedName || !datePeriod) return
		if (datePeriod === "custom" && (!dateFrom || !dateTo)) {
			toast.error("Choose both custom dates")
			return
		}

		const payload: ITransactionFilterPresetWrite = {
			name: trimmedName,
			date_period: datePeriod,
			...(datePeriod === "custom" ? { date_from: dateFrom, date_to: dateTo } : {}),
			filter: withoutComputedFields(currentFilter),
		}

		setBusy(true)
		try {
			const response = editorMode === "edit" && selectedPreset
				? await GD.REQ_COMPANY_TRANSACTION_PRESET_UPDATE.request({ uid: selectedPreset.uid, ...payload })
				: await GD.REQ_COMPANY_TRANSACTION_PRESET_CREATE.request(payload)
			if (response.error) throw response.error
			setEditorMode(null)
			await loadPresets(response.data?.uid)
			toast.success(editorMode === "edit" ? "Preset updated" : "Preset saved")
		} catch (error) {
			toast.error(getFriendlyErrorMessage(error, "Could not save preset"))
		} finally {
			setBusy(false)
		}
	}

	const remove = async () => {
		if (!selectedPreset?.can_delete) return
		if (!deleteArmed) {
			setDeleteArmed(true)
			return
		}
		setBusy(true)
		try {
			const response = await GD.REQ_COMPANY_TRANSACTION_PRESET_DELETE.request({ uid: selectedPreset.uid })
			if (response.error) throw response.error
			setDeleteArmed(false)
			await loadPresets()
			toast.success("Preset deleted")
		} catch (error) {
			toast.error(getFriendlyErrorMessage(error, "Could not delete preset"))
		} finally {
			setBusy(false)
		}
	}

	const smallButtonSx = { minHeight: "36px", padding: "0 14px", borderRadius: "10px", [MOBILE]: { minHeight: "44px" } } as const

	return (
		<VBox gap="12px" sx={{ "& select, & input": { minHeight: "36px" } }}>
			<HBox gap="8px" sx={{ alignItems: "center", flexWrap: "wrap" }}>
				<HBox gap="6px" sx={{ alignItems: "center", marginRight: "4px" }}>
					<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={DS.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
						<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
					</svg>
					<Typo sx={{ color: DS.muted, fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap" }}>Saved filters</Typo>
				</HBox>
				<select aria-label="Saved transaction preset" value={selectedUid} onChange={(event) => choosePreset(event.target.value)} style={{ ...controlStyle, flex: "1 1 200px", maxWidth: "340px" }}>
					<option value="">{presets.length ? "Choose saved filter" : "No saved filters"}</option>
					{presets.map((preset) => <option key={preset.uid} value={preset.uid}>{preset.name} · {preset.author_name}</option>)}
				</select>
				<Button variant="secondarySmall" sx={smallButtonSx} disabled={datePeriodOptions.length === 0 || busy} onClick={beginCreate}>Save filters</Button>
				{selectedPreset?.can_edit && <Button variant="secondarySmall" sx={smallButtonSx} disabled={busy} onClick={beginEdit}>Edit</Button>}
				{selectedPreset?.can_delete && <Button variant="secondarySmall" sx={{ ...smallButtonSx, color: "#B91C1C" }} disabled={busy} onClick={() => void remove()}>{deleteArmed ? "Confirm delete" : "Delete"}</Button>}
				{deleteArmed && <Button variant="secondarySmall" sx={smallButtonSx} disabled={busy} onClick={() => setDeleteArmed(false)}>Cancel</Button>}
			</HBox>

			{editorMode && (
				<Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px", alignItems: "end", padding: "14px", border: `1px solid ${DS.line}`, borderRadius: "12px", backgroundColor: DS.surface, animation: "v2-enter .24s ease both", "@media (max-width: 700px)": { gridTemplateColumns: "minmax(0, 1fr)" }, "& label": { display: "flex", flexDirection: "column", gap: "6px", color: DS.muted, fontSize: "13px", fontWeight: 500 } }}>
					<label>Name<input aria-label="Preset name" value={name} onChange={(event) => setName(event.target.value)} maxLength={120} placeholder="e.g. Weekly card spending" style={{ ...controlStyle, width: "100%" }} /></label>
					<label>Date period<select aria-label="Preset date period" value={datePeriod} onChange={(event) => setDatePeriod(event.target.value as TTransactionDatePeriod)} style={{ ...controlStyle, width: "100%" }}>
						{datePeriodOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
					</select></label>
					{datePeriod === "custom" && <><label>From<input aria-label="Preset date from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} style={controlStyle} /></label><label>To<input aria-label="Preset date to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} style={controlStyle} /></label></>}
					<HBox gap="8px" sx={{ gridColumn: "1 / -1" }}>
						<Button variant="primarySmall" disabled={busy || !name.trim() || !datePeriod} onClick={() => void submit()}>{busy ? "Saving…" : editorMode === "edit" ? "Update" : "Save"}</Button>
						<Button variant="secondarySmall" disabled={busy} onClick={() => setEditorMode(null)}>Cancel</Button>
					</HBox>
				</Box>
			)}
		</VBox>
	)
}
